import { ConnectingGroupArgs } from './connectingGroupArgs.js'
import { ShapeConnectGroups } from './shapeConnectGroups.js'

import { ShapeFillSeriesArgs } from './shapeFillSeriesArgs.js'
import { ShapeFillSeries } from './shapeFillSeries.js'

import { ShapeRadialGroupArgs } from './shapeRadialGroupArgs.js'
import { ShapeRadialGroups } from './shapeRadialGroups.js'

import { ShapeFillInterleaveArgs } from './shapeFillInterleaveArgs.js'
import { ShapeFillInterleave } from './shapeFillInterleave.js'

import { ShapeRadialSeriesArgs } from './shapeRadialSeriesArgs.js'
import { ShapeRadialSeries } from './shapeRadialSeries.js'

import { ConnectingSeriesArgs } from './connectingSeriesArgs.js'
import { ShapeConnectSeries } from './shapeConnectSeries.js'

import { ShapeFillRandomArgs } from './shapeFillRandomArgs.js'
import { ShapeFillRandom } from './shapeFillRandom.js'

import { ParameterCircle } from './parameterCircle.js'

// Functions here are meant to expedite filling shapes with various patterns
// or using the various other decoration classes.  
// They should all return just the resulting fill to make things simple

let connectGroupHelper = function(shape, groups, gaps, sweep, size=[1], offsets=[0]) {
    let shapePatternGroups = new ShapeConnectGroups(shape)
    let connectGroupArgs = new ConnectingGroupArgs()
    offsets = offsets.length === groups.length ? offsets : Array(groups.length).fill(offsets[0])
    connectGroupArgs.setGroups(groups)
        .setGroupGaps(gaps)
        .setGroupArcs(sweep.map(val => [val, val]))
        .setGroupOffsets(offsets)
    if (size.some((val, idx, arr) => val !== 0)) {
        connectGroupArgs.setGroupSizes(size.map(val => [val, val]))
    }
    let [connectingShapes, pts] = shapePatternGroups.getConnectingGroups(connectGroupArgs)
    return connectingShapes 
}

let connectSeriesHelper = function(
    shape, 
    arc, 
    step, 
    size, 
    gap, 
    maxConnect=0, 
    arcChange=[0, 0], 
    stepChange=[0, 0],
    sizeChange=[0, 0], 
    arcAdjust=[1, 1], 
    arcChangeRepeat=[[1, 1]], 
    stepChangeRepeat=[[1, 1]]
) {
    let shapeConnectSeries = new ShapeConnectSeries(shape)
    let connectSeriesArgs = new ConnectingSeriesArgs(arc, step, size, gap)
    connectSeriesArgs
        .setArcChange(arcChange)
        .setStepChange(stepChange)
        .setSizeChange(sizeChange)
        .setArcAdjust(arcAdjust)
        .setArcChangeRepeat(arcChangeRepeat)
        .setStepChangeRepeat(stepChangeRepeat)
    if (maxConnect > 0) {
        connectSeriesArgs.setMaxConnect(maxConnect)
    }
    let [connectingShapes, pts] = shapeConnectSeries.getConnecting(connectSeriesArgs)
    return connectingShapes 
}

const fillSeriesHelper = function(shape, curveCount, maxLayers, thickness, coverage, centerPt, change=0, rotation=0, offset=0, smooth=false, spacing=0, sizeFactors=[1, 1]) {
    let shapeFillSeries = new ShapeFillSeries(shape)
    let shapeFillSeriesArgs = new ShapeFillSeriesArgs(curveCount, offset, maxLayers, thickness, coverage, centerPt)
    shapeFillSeriesArgs
        .setChange(change)
        .setSmooth(smooth)
        .setRotation(rotation)
        .setSpacing(spacing)
        .setSizeFactors(sizeFactors)
    let [connectingShapes, pts] = shapeFillSeries.fillShape(shapeFillSeriesArgs)
    return connectingShapes 
}

const fillRandomHelper = function(shape, centerPt, layers, coverage, thickness, curveRange=[3,6], sizeFactors=[2,4], coverageRange=[2,4], change=0, spacing=0, disjointProtection=false, resolution=0.01) {
    let shapeFillRandom = new ShapeFillRandom(shape, centerPt)
    let shapeFillRandomArgs = new ShapeFillRandomArgs(layers, coverage, thickness, curveRange)
    shapeFillRandomArgs
        .setSizeFactors(sizeFactors)
        .setCoverageRange(coverageRange)
        .setChange(change)
        .setSpacing(spacing)
        .setDisjointProtection(disjointProtection)
        .setHiRes(resolution)
    let [connectingShapes, pts] = shapeFillRandom.fillShapeRandomArcs(shapeFillRandomArgs)
    return connectingShapes 
}

let radialGroupHelper = function(shape, groups, centers, groupRadii, groupSweep, groupRadiusRepeat=[[1, 1]], groupSweepRepeat=[[1, 1]], centered=true) {
    let shapeRadialGroups = new ShapeRadialGroups(shape)
    let radialGroupArgs = new ShapeRadialGroupArgs()
    radialGroupArgs.setGroups(groups)
        .setCenterPts(centers)
        .setIsCentered(centered)
        .setGroupRadii(groupRadii)
        .setGroupArcs(groupSweep)
        .setRadiusChangeRepeat(groupRadiusRepeat)
        .setSizeChangeRepeat(groupSweepRepeat)
    let connectingShapes = shapeRadialGroups.getRadials(radialGroupArgs)
    return connectingShapes 
}


let radialSeriesHelper = function(shape, arc, step, radius, centerPt, arcChangeRepeat=[1,1], radiusChangeRepeat=[1,1], maxRadials=0) {
    let shapeRadialSeries = new ShapeRadialSeries(shape)
    let radialSeriesArgs = new ShapeRadialSeriesArgs(arc, step, radius)
    radialSeriesArgs
        .setCenterPt(centerPt)
        .setArcChangeRepeat(arcChangeRepeat)
        .setRadiusChangeRepeat(radiusChangeRepeat)
    if (maxRadials > 0) {
        radialSeriesArgs.setMaxRadials(maxRadials)
    }
    let connectingShapes = shapeRadialSeries.getRadials(radialSeriesArgs)
    return connectingShapes 
}


let shapeFillInterleaveHelper = function(shape, curveCount, maxLayers, thickness, coverage, centerPt, innerPct=0.5, alternateDiff=0.5, rotation=0, spacing=[0, 0]) {
    let shapeFillInterleave = new ShapeFillInterleave(shape)
    let interleaveArgs = new ShapeFillInterleaveArgs(curveCount, maxLayers, thickness, coverage, centerPt)
    interleaveArgs
        .setInnerPct(innerPct)
        .setAlternateDiff(alternateDiff)
        .setRotation(rotation)
        .setSpacing(spacing)
    let [shapes, pts] = shapeFillInterleave.fillShape(interleaveArgs)
    return shapes
}


// Create radiating bands to serve as a flourish when hovering a link
const makeRadiatingCurves = function(draw, centerPt, circleRadius, layers, coverage, thickness, hsl, hslVary, id, gap=0.035) {
    const centerCircle = new ParameterCircle(centerPt.x, centerPt.y, circleRadius)
    const randomCurves = fillRandomHelper(centerCircle, centerCircle.centerPt,  layers, coverage, thickness, [3, 5], [3, 5], [1, 1], 0, gap)

    const styleFill = new Map([['fill-opacity', 0.9], ['fill', 'hsl(336, 30, 93, 1.0)'], ['stroke', 'none']])
    const [curveGroup, curveRadiateIds] = draw.drawPathsColorSequenceRand(`link-radiate-curves-${id}`, randomCurves, styleFill, false, hsl, hslVary, 0.33, 'fill')
    return {'fillIds': curveRadiateIds, 'fillGroup': curveGroup}
}


export { connectGroupHelper, fillSeriesHelper, radialGroupHelper, shapeFillInterleaveHelper, radialSeriesHelper, connectSeriesHelper, fillRandomHelper, makeRadiatingCurves }
